import { useEffect, useRef } from 'react';

const ACTIVITY_INTERVAL_MS = 10_000;

/** Batches real interactions; never keeps an idle or completed checkout alive. */
export function useCheckoutActivity(record: (() => void | Promise<unknown>) | undefined, enabled: boolean) {
  const latest = useRef(record);
  latest.current = record;
  const lastSent = useRef(Number.NEGATIVE_INFINITY);
  const lastInteraction = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => {
    clearTimeout(timer.current);
    timer.current = undefined;
    lastSent.current = Number.NEGATIVE_INFINITY;
  }, [enabled]);

  return () => {
    if (!enabled || latest.current === undefined) return;
    lastInteraction.current = Date.now();
    const send = () => {
      timer.current = undefined;
      // A suspended tab must not turn an old interaction into fresh activity.
      if (Date.now() - lastInteraction.current > ACTIVITY_INTERVAL_MS + 1_000) return;
      lastSent.current = Date.now();
      try {
        void Promise.resolve(latest.current?.()).catch(() => undefined);
      } catch {
        // Activity tracking is best-effort and must not interrupt the form.
      }
    };
    if (Date.now() - lastSent.current >= ACTIVITY_INTERVAL_MS) {
      clearTimeout(timer.current);
      send();
    } else if (timer.current === undefined) {
      timer.current = setTimeout(send, ACTIVITY_INTERVAL_MS - (Date.now() - lastSent.current));
    }
  };
}
